const capitalize = (str = "") => {
  if (typeof str !== "string") throw new Error("The input must be a string");
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const toTitleCase = (str, separator) => {
  return str
    .split(separator || " ")
    .map(capitalize)
    .join(" ");
};

export function formatMillis(ms) {
  let seconds = Math.floor(ms / 1000);

  const days = Math.floor(seconds / 86400);
  seconds %= 86400;

  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;

  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  const parts = [];

  if (days) parts.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hours) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
  if (minutes) parts.push(`${minutes} min`);
  if (!parts.length && seconds)
    parts.push(`${seconds} sec`);

  return parts.join(' ');
}

const toSnakeCase = (str, separator) => {
  return str
    .split(separator || " ")
    .join("_")
    .toLowerCase();
};

const toCamelCase = (str, separator) => {
  return str
    .split(separator || " ")
    .map((word, index) => {
      if (index === 0) return word.toLowerCase();
      return capitalize(word);
    })
    .join("");
};

const toKebabCase = (str, separator) => {
  return str
    .split(separator || " ")
    .join("-")
    .toLowerCase();
};

const truncate = (str, length) => {
  return str.length > length ? str.slice(0, length) + "..." : str;
};

const slugify = (str, separator) => {
  return str.toLowerCase().replace(separator || /\s/g, "-");
};

const generateUUID = () => {
  const date_anchor =
    Math.random().toString(36).substring(2) + Date.now().toString(36);
  const uuid = "xxxx-xxxx-xxx-xxxx".replace(/x/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return r.toString(16);
  });
  return `${date_anchor}-${uuid}`;
};

const maskString = (str) => {
  const parts = str.split(/(\.|-)/);

  if (parts.length === 1) return str;

  const maskedParts = parts.slice(1, parts.length - 1).map((part, index) => {
    if (/[a-zA-Z0-9]/.test(part)) {
      return index === 0 || index === parts.length - 1
        ? part
        : part.replace(/[a-zA-Z0-9]/g, "X");
    }
    return part;
  });

  const wholeMaskedParts = [parts[0], ...maskedParts, parts[parts.length - 1]];

  return wholeMaskedParts.join("");
};

/**
 * Build a query string
 * @param {string} key
 * @param {object} object
 * @returns {string}
 * @example
 * buildQueryString("data", { name: "John" });
 * output: "data=%7B%22name%22%3A%22John%22%7D"
 * @description
 * This function is used to build a query string.
 * The object is stringified and then URL encoded.
 * The function returns the query string.
 * The query string can be parsed using the `parseQueryString` function.
 * @see parseQueryString
 */
const buildQueryString = (key, object) => {
  const queryObject = {
    [key]: JSON.stringify(object),
  };
  return new URLSearchParams(queryObject).toString();
};

/**
 * Parse a query string
 * @param {string} key
 * @param {string} queryString
 * @returns {object}
 * @example
 * parseQueryString("data", "data=%7B%22name%22%3A%22John%22%7D");
 * output: { name: "John" }
 * @description
 * This function is used to parse a query string.
 * The query string is URL decoded and then parsed as JSON.
 * The function returns the parsed object.
 * The query string can be built using the `buildQueryString` function.
 * @see buildQueryString
 */
const parseQueryString = (key, queryString) => {
  const queryObject = Object.fromEntries(new URLSearchParams(queryString));
  return JSON.parse(queryObject[key]);
};

export {
  capitalize,
  toTitleCase,
  truncate,
  slugify,
  toSnakeCase,
  toCamelCase,
  toKebabCase,
  generateUUID,
  buildQueryString,
  parseQueryString,
  maskString,
};
